import * as Sentry from '@sentry/browser'

import { buildSegmentSnippet } from './buildSegmentSnippet'

const SEGMENT_SCRIPT_ID = 'segment-analytics-script'

const createSegmentScript = (snippet: string): HTMLScriptElement => {
  const script = document.createElement('script')

  script.id = SEGMENT_SCRIPT_ID
  script.type = 'text/javascript'
  script.innerHTML = snippet

  return script
}

const appendSegmentScript = (scriptElement: HTMLScriptElement) => {
  document.head.appendChild(scriptElement)
}

const hasScriptTag = () => Boolean(document.getElementById(SEGMENT_SCRIPT_ID))

const logMissingAPIKey = (isProduction: boolean) => {
  // Only log to Sentry in production where this is an actual misconfiguration
  if (!isProduction) {
    return
  }

  Sentry.withScope((scope) => {
    scope.setLevel('warning')
    scope.setTag('component', 'GlobalAnalytics')
    Sentry.captureMessage('Segment API key is not defined.')
  })
}

/**
 * Injects the Segment analytics script into the document head if it hasn't been injected already.
 * Returns true if Segment is ready to use (either newly injected or already present).
 * Returns false if the provided API key is missing, and logs a warning to Sentry in production.
 *
 * @param apiKey - The Segment API key to use for initializing analytics.
 * @param isProduction - Flag indicating if the environment is production, used to determine if Sentry logging should occur.
 * @returns boolean indicating whether Segment is ready to use.
 */
export const injectSegment = (
  apiKey: string | undefined,
  isProduction: boolean = false
): boolean => {
  if (!apiKey) {
    logMissingAPIKey(isProduction)
    return false
  }

  if (hasScriptTag()) {
    return true
  }

  const snippet = buildSegmentSnippet(apiKey)
  const scriptElement = createSegmentScript(snippet)
  appendSegmentScript(scriptElement)

  return true
}
